import React, { useContext, useEffect, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import { MAIN_DASHBOARD_QUERY, GET_STUDENT_READING_LOGS_TARGET } from './services/graphql';
import { Loader } from '@squirrellearningltd/go-pupil-components';
import ReadingBooks from './components/ReadingBooks';
import { AuthContext } from '../../services/user/AuthProvider';
import moment from 'moment';

const Dashboard = () => {
    // Weekly reading targets
    const getStartOfWeek = (startDay) => {
        const date = new Date();
        // Convert backend day (1–7, where 1 = Monday) to JS day (0–6, where 0 = Sunday)
        const jsStartDay = startDay % 7;
      
        const currentDay = date.getDay(); // JS weekday (0 = Sunday, ..., 6 = Saturday)
        
        // Calculate distance to go back to the desired start of the week
        const diff = (currentDay - jsStartDay + 7) % 7;
      
        // Subtract the difference to get the starting date
        const startOfWeek = new Date(date);
        startOfWeek.setDate(date.getDate() - diff);

        return startOfWeek;
    }

    const [studentReadingTarget, setStudentReadingTarget] = useState();
    const [studentTargetProgress, setStudentTargetProgress] = useState();
    const [parentReadingTarget, setParentReadingTarget] = useState();
    const [parentTargetProgress, setParentTargetProgress] = useState();

    const { setCurrentUser } = useContext(AuthContext);

    const { data, error, loading } = useQuery(MAIN_DASHBOARD_QUERY, {
        variables: {
            date: moment.utc().format('DD/MM/YYYY')
        },
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            data && data.currentStudent && setCurrentUser(data.currentStudent)
        }
    });

    const [getReadingTargetsForStudent, {loading: studentReadingTargetLoading}] = useLazyQuery(GET_STUDENT_READING_LOGS_TARGET, {
        fetchPolicy: 'network-only',
        onCompleted: ({currentStudent}) => {
            if (currentStudent) {
                const hasStudentTarget = currentStudent && currentStudent.studentWeeklyTarget ? currentStudent.studentWeeklyTarget.studentLogsTarget : false;
                const hasParentTarget = currentStudent && currentStudent.studentWeeklyTarget ? currentStudent.studentWeeklyTarget.parentLogsTarget : false;
                if (currentStudent.weeklyStudentReportData && currentStudent.weeklyStudentReportData.length) {
                    const weekStart = getStartOfWeek(data.currentStudent.school.weekStart);
                    if (hasStudentTarget) {
                        const currentWeekData = currentStudent.weeklyStudentReportData.find((weeklyReportData) => weeklyReportData.weekStart === moment(weekStart).format('YYYY-MM-DD'));
                        if (currentWeekData) {
                            setStudentReadingTarget(currentStudent.studentWeeklyTarget.studentLogsTarget);
                            setStudentTargetProgress(currentWeekData.studentLogsCount ? currentWeekData.studentLogsCount : 0);
                        }
                    }
        
                    if (hasParentTarget) {
                        const currentWeekData = currentStudent.weeklyStudentReportData.find((weeklyReportData) => weeklyReportData.weekStart === moment(weekStart).format('YYYY-MM-DD'));
                        if (currentWeekData) {
                            setParentReadingTarget(currentStudent.studentWeeklyTarget.parentLogsTarget);
                            setParentTargetProgress(currentWeekData.parentLogsCount ? currentWeekData.parentLogsCount : 0);
                        }
                    }
                } else {
                    const studentLogsTarget =
                        currentStudent.studentWeeklyTarget &&
                        currentStudent.studentWeeklyTarget.studentLogsTarget ? currentStudent.studentWeeklyTarget.studentLogsTarget : 0;
                    setStudentReadingTarget(studentLogsTarget);
                    setStudentTargetProgress(0);
                    
                    const parentLogsTarget =
                        currentStudent.studentWeeklyTarget &&
                        currentStudent.studentWeeklyTarget.parentLogsTarget ?
                        currentStudent.studentWeeklyTarget.parentLogsTarget : 0;
                    setParentReadingTarget(parentLogsTarget);
                    setParentTargetProgress(0);
                }
            }
        }
    });

    useEffect(() => {
        if (data && data.currentStudent) {
            getReadingTargetsForStudent({
                variables: {
                    weekStart: moment(getStartOfWeek(data.currentStudent.school.weekStart)).format('DD/MM/YYYY'),
                }
            })
        }
    }, [data])

    if (error) {
        return <p>{error.message}</p>
    }

    if (loading && !data) {
        return (
            <div className="page-wrapper">
                <div className="page-wrapper__main">
                    <div className="content-panel">
                        <Loader />
                    </div>
                </div>
            </div>
        )
    }

    if (data && data.currentStudent && data.currentStudent.readingBooks) {
        const allBooks = [...data.currentStudent.readingBooks, ...data.currentStudent.groupReadingBooks];
        const targets = {
            student: {
                target: studentReadingTarget,
                progress: studentTargetProgress
            },
            parent: {
                target: parentReadingTarget,
                progress: parentTargetProgress
            },
            loading: studentReadingTargetLoading
        };

        return <ReadingBooks
            student={data.currentStudent}
            readingBooks={allBooks}
            targets={targets}
        />
    }

    return null;

}

export default Dashboard;
